import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { imageUrl } from "@/lib/api";
import { ProductImagePlaceholder } from "@/components/site/Logo";
import { Reveal } from "@/components/site/Reveal";

export default function Cart() {
  const { cart, updateQuantity, removeItem, cartSubtotal, settings } = useShop();
  const navigate = useNavigate();
  const shipping = settings?.shipping_flat ?? 10.5;

  if (cart.length === 0) {
    return (
      <div className="pt-40 pb-40 text-center px-6" data-testid="cart-page-empty">
        <ShoppingBag size={48} strokeWidth={1} className="text-[#D4AF37]/60 mx-auto" />
        <h1 className="font-serif-display text-4xl text-white mt-8">Your bag is empty</h1>
        <p className="font-sans-body text-[#B8B8B8] font-light mt-4">
          Your signature scent is waiting to be discovered.
        </p>
        <button className="btn-gold mt-10" onClick={() => navigate("/shop")} data-testid="cart-page-continue-shopping">
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 max-w-6xl mx-auto px-4 sm:px-8 lg:px-16" data-testid="cart-page">
      <Reveal>
        <p className="eyebrow mb-3">Your Selection</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-white font-light">Shopping Bag</h1>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-12">
        <div className="lg:col-span-2 space-y-6">
          {cart.map((item) => (
            <div key={`${item.product_id}-${item.size_label}`}
              className="flex gap-6 gold-hairline bg-[#0D0D0D] p-5"
              data-testid={`cart-page-item-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}>
              <div className="w-24 h-32 shrink-0 overflow-hidden gold-hairline">
                {item.image_url ? (
                  <img src={imageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <ProductImagePlaceholder name={item.name} className="w-full h-full" />
                )}
              </div>
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-serif-display text-2xl text-white">{item.name}</h3>
                    <p className="font-sans-body text-[11px] uppercase tracking-[0.2em] text-[#C6A15B] mt-1">{item.size_label}</p>
                  </div>
                  <button onClick={() => removeItem(item.product_id, item.size_label)} aria-label="Remove item"
                    className="text-[#B8B8B8] hover:text-red-400 transition-colors"
                    data-testid={`cart-page-remove-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}>
                    <Trash2 size={17} strokeWidth={1.5} />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between pt-4">
                  <div className="flex items-center gold-hairline">
                    <button className="px-3 py-2 text-[#B8B8B8] hover:text-[#D4AF37]"
                      onClick={() => updateQuantity(item.product_id, item.size_label, item.quantity - 1)}
                      data-testid={`cart-page-minus-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`} aria-label="Decrease">
                      <Minus size={14} />
                    </button>
                    <span className="px-4 font-mono-price text-white">{item.quantity}</span>
                    <button className="px-3 py-2 text-[#B8B8B8] hover:text-[#D4AF37]"
                      onClick={() => updateQuantity(item.product_id, item.size_label, item.quantity + 1)}
                      data-testid={`cart-page-plus-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`} aria-label="Increase">
                      <Plus size={14} />
                    </button>
                  </div>
                  <span className="font-mono-price text-lg text-[#D4AF37]">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="gold-hairline bg-[#0D0D0D] p-8 h-fit space-y-5" data-testid="cart-summary">
          <h2 className="font-serif-display text-2xl text-white">Order Summary</h2>
          <div className="flex justify-between font-sans-body text-sm text-[#B8B8B8]">
            <span>Subtotal</span>
            <span className="font-mono-price text-white" data-testid="cart-page-subtotal">${cartSubtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-sans-body text-sm text-[#B8B8B8]">
            <span>Shipping (standard)</span>
            <span className="font-mono-price text-white">${shipping.toFixed(2)}</span>
          </div>
          <p className="font-sans-body text-[11px] text-[#666666] font-light">
            Taxes and express shipping calculated at checkout.
          </p>
          <div className="flex justify-between gold-hairline-t pt-5">
            <span className="font-sans-body text-sm uppercase tracking-[0.2em] text-white">Estimated Total</span>
            <span className="font-mono-price text-xl text-[#D4AF37]" data-testid="cart-page-total">
              ${(cartSubtotal + shipping).toFixed(2)}
            </span>
          </div>
          <button className="btn-gold w-full" onClick={() => navigate("/checkout")} data-testid="cart-page-checkout-button">
            Checkout <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
